import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAutoPaginationNavigation } from '@/utils/autoPaginationNavigation';
import { getErrorMessage } from '@/utils/errorUtils';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Link,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from '@mui/icons-material';
import Images from '@/assets/Images';
import styles from '../AssetManagement/AssetDetails/AssetDetails.module.scss';
import createStyles from '../AssetManagement/AssetCreate/AssetCreate.module.scss';
import activityService, { ActivityDetail, ActivityMovementSession, CompleteActivityData } from '../../services/activityService';
import { useCurrency } from '@/components/common/CurrencyFormat';
import { ActivityStatus } from '../../enums/schedule.enums';
import { formatDate as formatDateUtil } from '@/utils/formatters';
const ActivityExecutePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { backToListing } = useAutoPaginationNavigation(navigate, '/schedule-management/activities');
  
  // State management
  const [activity, setActivity] = useState<ActivityDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form data
  const [costs, setCosts] = useState<Array<{ label: string; value: number; templateItemId?: string; activityCostId?: string }>>([]);
  const [checklistItems, setChecklistItems] = useState<Array<{ 
    id: string; 
    label: string; 
    isCompleted: boolean; 
    templateItemId: string;
    activityChecklistId?: string;
  }>>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [comments, setComments] = useState<string>('');
  const [movementSessions, setMovementSessions] = useState<ActivityMovementSession[]>([]);
  const currencySymbol = useCurrency().currencySymbol;
  // Shown when a movement-tracked activity is missing its check-out/check-in
  // scan - the backend flags this with force_complete_required so the user
  // can confirm completing it anyway (comments above serve as the reason).
  const [forceCompleteConfirm, setForceCompleteConfirm] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  // Force-record a check-out/check-in step directly (independent of
  // completing the activity) - distinct from forceCompleteConfirm above,
  // which only force-closes missing steps as part of submitting completion.
  const [forceStepAction, setForceStepAction] = useState<'checkout' | 'checkin' | null>(null);
  const [forceStepComments, setForceStepComments] = useState('');
  const [forceStepSubmitting, setForceStepSubmitting] = useState(false);
  // Notification state
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Fetch activity details
  useEffect(() => {
    const fetchActivityDetails = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const activityData = await activityService.getActivityDetails(id);

        // Guard: activity must be IN_PROGRESS before it can be executed
        if (activityData.status === ActivityStatus.PENDING) {
          navigate('/schedule-management/activities', {
            state: { error: 'This activity must be started before it can be executed.' },
          });
          return;
        }

        setActivity(activityData);
        
        // Initialize costs using template as source of truth, merge with existing activity data
        if (activityData.cost_template_detail?.items) {
          const templateItems = activityData.cost_template_detail.items;
          const existingCosts = activityData.costs || [];
          
          const initialCosts = templateItems.map(templateItem => {
            // Find matching existing cost by template item ID
            const existingCost = existingCosts.find(cost =>
              cost.cost_template_item === templateItem.id
            );
            
            return {
              label: templateItem.label,
              value: existingCost?.value || 0,
              templateItemId: templateItem.id,
              activityCostId: existingCost?.id, // Store the actual ActivityCost ID if exists
            };
          });
          setCosts(initialCosts);
        }
        
        // Initialize checklist using template as source of truth, merge with existing activity data
        if (activityData.checklist_template_detail?.items) {
          const templateItems = activityData.checklist_template_detail.items;
          const existingChecklistItems = activityData.checklist_items || [];
          
          const initialChecklist = templateItems.map(templateItem => {
            // Find matching existing checklist item by template item ID
            const existingItem = existingChecklistItems.find(item =>
              item.checklist_template_item === templateItem.id
            );
            
            return {
              id: existingItem?.id || `temp-${templateItem.id}`,
              label: templateItem.question,
              isCompleted: existingItem?.is_completed || false,
              templateItemId: templateItem.id,
              activityChecklistId: existingItem?.id, // Store the actual ActivityChecklist ID if exists
            };
          });
          setChecklistItems(initialChecklist);
        }
        
      } catch (err) {
        console.error('Error fetching activity details:', err);
        setError('Failed to load activity details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchActivityDetails();
  }, [id]);

  const fetchMovementSessions = async () => {
    if (!id || !activity?.movement_tracking_required) {
      setMovementSessions([]);
      return;
    }

    try {
      const sessions = await activityService.getActivityMovementSessions(id);
      setMovementSessions(sessions);
    } catch (err) {
      console.error('Error fetching movement sessions:', err);
      setMovementSessions([]);
    }
  };

  // Fetch the check-out/check-in scan history for movement-tracked
  // activities, so the user can see what's missing before attempting to
  // complete (rather than only finding out from the force-complete error).
  useEffect(() => {
    fetchMovementSessions();
  }, [id, activity?.movement_tracking_required]);

  const hasAuthorizedMovement = (statuses: string[]) =>
    movementSessions.some((session) => session.is_authorized && statuses.includes(session.movement_status || ''));
  const hasCheckout = hasAuthorizedMovement(['checkout', 'force_checkout']);
  const hasCheckin = hasAuthorizedMovement(['checkin', 'force_checkin']);

  const handleOpenForceStepDialog = (action: 'checkout' | 'checkin') => {
    setForceStepAction(action);
    setForceStepComments('');
  };

  const handleCloseForceStepDialog = () => {
    setForceStepAction(null);
    setForceStepComments('');
  };

  const handleForceStepSubmit = async () => {
    if (!forceStepAction || !forceStepComments.trim() || !activity) return;

    try {
      setForceStepSubmitting(true);
      const updated = forceStepAction === 'checkout'
        ? await activityService.forceCheckoutActivity(activity.id, forceStepComments.trim())
        : await activityService.forceCheckinActivity(activity.id, forceStepComments.trim());
      setActivity(updated);
      await fetchMovementSessions();
      setNotification({
        open: true,
        message: forceStepAction === 'checkout'
          ? 'Activity force-checked-out successfully!'
          : 'Activity force-checked-in successfully!',
        severity: 'success',
      });
      handleCloseForceStepDialog();
    } catch (err) {
      console.error('Error force-closing activity movement step:', err);
      setNotification({
        open: true,
        message: getErrorMessage(err, `Failed to force ${forceStepAction === 'checkout' ? 'check-out' : 'check-in'}.`),
        severity: 'error',
      });
    } finally {
      setForceStepSubmitting(false);
    }
  };

  // Handle cost changes
  const handleCostChange = (index: number, value: string) => {
    const numericValue = parseFloat(value) || 0;
    setCosts(prev => prev.map((cost, i) => 
      i === index ? { ...cost, value: numericValue } : cost
    ));
  };

  // Handle checklist changes
  const handleChecklistToggle = (index: number) => {
    setChecklistItems(prev => prev.map((item, i) => 
      i === index ? { ...item, isCompleted: !item.isCompleted } : item
    ));
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  // Remove file
  const handleFileRemove = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle form submission. forceComplete is set when the user has already
  // confirmed completing despite a missing check-out/check-in scan.
  const handleSubmit = async (forceComplete: boolean = false) => {
    if (!activity) return;

    try {
      setSubmitting(true);

      // Map costs to use activityCostId (the actual ActivityCost record ID)
      const costData = costs.map(cost => ({
        activityCostId: cost.activityCostId || '',
        value: cost.value,
      })).filter(cost => cost.activityCostId); // Only include costs with valid IDs

      // Map checklist items to use activityChecklistId (the actual ActivityChecklist record ID)
      const checklistData = checklistItems.map(item => ({
        activityChecklistId: item.activityChecklistId || item.templateItemId,
        isCompleted: item.isCompleted,
      }));

      const completionData: CompleteActivityData = {
        costs: costData,
        checklistItems: checklistData,
        files,
        comments,
        force_complete: forceComplete || undefined,
      };

      await activityService.completeActivity(activity.id, completionData);

      setForceCompleteConfirm({ open: false, message: '' });
      setNotification({
        open: true,
        message: 'Activity completed successfully!',
        severity: 'success',
      });

      // Navigate back after a short delay
      setTimeout(() => {
        backToListing();
      }, 2000);

    } catch (err: any) {
      console.error('Error completing activity:', err);

      if (!forceComplete && err.response?.status === 400 && err.response?.data?.force_complete_required) {
        setForceCompleteConfirm({
          open: true,
          message: err.response.data.detail || 'Check-out/check-in scan is missing for this activity. Complete it anyway?',
        });
        return;
      }

      setNotification({
        open: true,
        message: 'Failed to complete activity. Please try again.',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    backToListing();
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Format date function
  const formatDate = (dateString?: string) => {
    if (!dateString) return '--';
    return formatDateUtil(dateString, 'M/d/yyyy', dateString);
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '--';
    return formatDateUtil(dateString, 'M/d/yyyy, h:mm:ss a', dateString);
  };

  // Loading state
  if (loading) {
    return (
      <div className="appWrapper">
        <h3 className="pageTitle">
          <Link onClick={() => backToListing()} className="back">
            <img src={Images.BACK} alt="Back" width="22" />
          </Link>
          Complete Activity
        </h3>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  // Error state
  if (error || !activity) {
    return (
      <div className="appWrapper">
        <h3 className="pageTitle">
          <Link onClick={() => backToListing()} className="back">
            <img src={Images.BACK} alt="Back" width="22" />
          </Link>
          Complete Activity
        </h3>
        <Alert severity="error">
          {error || 'Activity not found'}
        </Alert>
      </div>
    );
  }

  const completedChecklistCount = checklistItems.filter(item => item.isCompleted).length;
  const checklistProgress = checklistItems.length > 0 ? (completedChecklistCount / checklistItems.length) * 100 : 0;

  return (
    <div className="appWrapper" style={{ paddingBottom: '160px' }}>
      <h3 className="pageTitle">
        <Link onClick={() => backToListing()} className="back">
          <img src={Images.BACK} alt="Back" width="22" />
        </Link>
        Complete Activity
      </h3>

      <div className="appGridContainer">
        <div className="gridWrapper">
          <div className={`${styles.assetDetWrapper}`}>
            <div className={`${styles.assetDetContainer} bordered r10`}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <h4>
                  {activity.task_id || activity.taskId}
                </h4>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <span className={`status ${activity.status === 'COMPLETED' ? 'active' : activity.status === 'IN_PROGRESS' ? 'active' : 'inactive'}`}>
                    {activity.status}
                  </span>
                </div>
              </div>

              <section className={`border_bottom ${styles.assetBasic}`}>
                <div className="d_flex">
                  <div className="col">
                    <label className="label">Activity Type</label>
                    <h5 className="body1">
                      {activity.activity_type?.name || '--'}
                    </h5>
                  </div>
                  <div className="col">
                    <label className="label">Location</label>
                    <h5 className="body1">
                      {activity.location?.name || '--'}
                    </h5>
                  </div>
                  <div className="col">
                    <label className="label">Assigned To</label>
                    <h5 className="body1">
                      {activity.assigned_user_name || '--'}
                    </h5>
                  </div>
                  <div className="col">
                    <label className="label">User Group</label>
                    <h5 className="body1">
                      {activity.user_group?.name || '--'}
                    </h5>
                  </div>
                </div>
              </section>

              <section>
                <div className="d_flex wrap">
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Due Date</label>
                    <h5 className="body1">
                      {formatDate(activity.due_date)}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Started</label>
                    <h5 className="body1">
                      {formatDateTime(activity.start_date)}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Schedule</label>
                    <h5 className="body1">
                      {activity.is_one_off ? 'One-Off Activity' : (activity.schedule?.name || activity.schedule_name || '--')}
                    </h5>
                  </div>

                  <div className="col_33 mt_10 mb_10">
                    <label className="label">SLA Status</label>
                    <h5 className="body1">
                      {activity.slaStatus || '--'}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Description</label>
                    <h5 className="body1">
                      {activity.schedule?.description || '--'}
                    </h5>
                  </div>
                </div>
              </section>

              {/* Asset Information Section */}
              {activity.asset && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Asset Information
                  </h4>
                  <div className="d_flex wrap">
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Asset Name</label>
                      <h5 className="body1">{activity.asset.asset_name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Asset Tag</label>
                      <h5 className="body1">{activity.asset.asset_tag || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Category</label>
                      <h5 className="body1">{activity.asset.asset_category_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Status</label>
                      <h5 className="body1">{activity.asset.asset_status_detail?.name || '--'}</h5>
                    </div>
                  </div>
                </section>
              )}

              {/* Cost Information Section */}
              {costs.length > 0 && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Cost Information
                  </h4>
                  <div style={{ marginBottom: '15px' }}>
                    <label className="label">Template: {activity.cost_template_name || 'Cost Template'}</label>
                  </div>

                  {/* Cost Items in bordered container */}
                  <div style={{ border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#fff', padding: '20px' }}>
                    <div className="d_flex wrap">
                      {costs.map((cost, index) => (
                        <div key={index} className="col_33 mt_10 mb_10" style={{ paddingRight: '15px' }}>
                          <label className="label" style={{ marginBottom: '8px', display: 'block', fontWeight: '500' }}>
                            {cost.label}
                          </label>
                          <TextField
                            fullWidth
                            type="number"
                            placeholder="0.00"
                            value={cost.value || ''}
                            onChange={(e) => handleCostChange(index, e.target.value)}
                            variant="outlined"
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1, color: '#666', fontWeight: '500' }}>{currencySymbol}</Typography>,
                            }}
                            inputProps={{
                              min: 0,
                              step: 0.01
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                border: '1px solid #e0e0e0',
                                minHeight: '48px',
                                '&:hover': {
                                  backgroundColor: '#fff',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: '#fff',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: 'none',
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                padding: '12px 14px',
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#333',
                              },
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {/* Checklist Section */}
              {checklistItems.length > 0 && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Checklist
                  </h4>
                  <div style={{ marginBottom: '15px' }}>
                    <label className="label">Template: {activity.checklist_template_name || 'Checklist Template'}</label>
                  </div>
                  
                  {/* Progress Bar */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={checklistProgress}
                        color="success"
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography variant="body2" color="text.secondary">
                        {`${completedChecklistCount}/${checklistItems.length}`}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {`${Math.round(checklistProgress)}% completed`}
                  </Typography>

                  {/* Checklist Items */}
                  <div className={`${createStyles.formContainer} ${createStyles.formRowContainer}`}>
                    {checklistItems.map((item, index) => (
                      <div key={item.id} className={createStyles.formFields}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={item.isCompleted}
                              onChange={() => handleChecklistToggle(index)}
                              color="success"
                              sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }}
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  textDecoration: item.isCompleted ? 'line-through' : 'none',
                                  color: item.isCompleted ? 'text.secondary' : 'text.primary',
                                  fontWeight: item.isCompleted ? 'normal' : 'medium',
                                  fontSize: '14px'
                                }}
                              >
                                {item.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: item.isCompleted ? 'success.main' : 'warning.main',
                                  display: 'block',
                                  mt: 0.5,
                                  fontSize: '12px'
                                }}
                              >
                                {item.isCompleted ? 'Completed' : 'Not completed'}
                              </Typography>
                            </Box>
                          }
                          sx={{
                            width: '100%',
                            alignItems: 'flex-start',
                            bgcolor: item.isCompleted ? 'rgba(76, 175, 80, 0.08)' : 'transparent',
                            padding: '8px',
                            borderRadius: '4px',
                            transition: 'background-color 0.3s'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Movement Tracking Section */}
              {activity.movement_tracking_required && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Movement Tracking
                  </h4>
                  <div className="d_flex wrap">
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Tracking Type</label>
                      <h5 className="body1">{activity.tracking_type_display || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Check-out</label>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {hasCheckout ? (
                          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        ) : (
                          <HourglassEmptyIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                        )}
                        <Typography variant="body2">{hasCheckout ? 'Recorded' : 'Pending'}</Typography>
                      </Box>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Check-in</label>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {hasCheckin ? (
                          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        ) : (
                          <HourglassEmptyIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                        )}
                        <Typography variant="body2">{hasCheckin ? 'Recorded' : 'Pending'}</Typography>
                      </Box>
                    </div>
                  </div>
                  {activity.status === 'IN_PROGRESS' && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      {!hasCheckout && (
                        <Button variant="outlined" color="warning" onClick={() => handleOpenForceStepDialog('checkout')}>
                          Force Check-out
                        </Button>
                      )}
                      {!hasCheckin && (hasCheckout || activity.is_one_off) && (
                        <Button variant="outlined" color="warning" onClick={() => handleOpenForceStepDialog('checkin')}>
                          Force Check-in
                        </Button>
                      )}
                    </Box>
                  )}
                  {(() => {
                    // Mirrors the backend's blocking rule (schedule/views.py
                    // ActivityViewSet.complete): a missing check-out only
                    // blocks scheduled activities, not one-off ones; a
                    // missing check-in always blocks.
                    const missingLabels = [
                      !hasCheckout && !activity.is_one_off ? 'check-out' : null,
                      !hasCheckin ? 'check-in' : null,
                    ].filter(Boolean);
                    if (missingLabels.length === 0) return null;
                    return (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        This activity is missing a {missingLabels.join(' and ')} scan.
                        Completing it will require a Force Complete with comments.
                      </Alert>
                    );
                  })()}
                </section>
              )}

              {/* Execution Comments Section */}
              <section className="border_bottom pb_30 pt_30">
                <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                  Execution Comments
                </h4>
                <div className={createStyles.formContainer}>
                  <div className={`${createStyles.formFields} ${createStyles.fullWidth}`}>
                    <label>Comments</label>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Describe the work performed and any observations"
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      variant="outlined"
                    />
                  </div>
                </div>
              </section>

              {/* File Attachments Section */}
              <section className="pb_30 pt_30">
                <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                  File Attachments
                </h4>
                
                {/* Upload Button */}
                <div className="col_100 mt_10 mb_10">
                  <input
                    accept="*/*"
                    style={{ display: 'none' }}
                    id="file-upload"
                    multiple
                    type="file"
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      sx={{
                        borderColor: '#dbe2e9',
                        color: '#576473',
                        '&:hover': {
                          borderColor: 'primary.main',
                          color: 'primary.main',
                        },
                      }}
                    >
                      Upload Files
                    </Button>
                  </label>
                </div>

                {/* File List */}
                <div className="col_100 mt_10 mb_10">
                  {files.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {files.map((file, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            backgroundColor: '#eff3f6'
                          }}
                        >
                          <AttachFileIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <div style={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ color: 'primary.main' }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(file.size / 1024).toFixed(1)} KB
                            </Typography>
                          </div>
                          <IconButton
                            size="small"
                            onClick={() => handleFileRemove(index)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No files uploaded
                    </Typography>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Footer */}
      <Box
        sx={{
          position: 'fixed',
          bottom: '48px',
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          backgroundColor: '#fdfdfd',
          borderTop: '1px solid #e0e0e0',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Button 
          variant="outlined" 
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSubmit()}
          disabled={submitting}
          className="button button__solid--primary"
          sx={{
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          {submitting ? <CircularProgress size={24} color="inherit" /> : 'Complete Activity'}
        </Button>
      </Box>

      {/* Force Complete confirmation */}
      <Dialog
        open={forceCompleteConfirm.open}
        onClose={() => setForceCompleteConfirm({ open: false, message: '' })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Complete without full movement history?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{forceCompleteConfirm.message}</DialogContentText>
          <TextField
            fullWidth
            required
            multiline
            rows={3}
            label="Comments"
            placeholder="Explain why this activity is being completed without a full check-out/check-in scan"
            value={comments}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setComments(e.target.value)}
            error={!comments.trim()}
            helperText={!comments.trim() ? 'Comments are required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceCompleteConfirm({ open: false, message: '' })} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            disabled={submitting || !comments.trim()}
            variant="contained"
            color="warning"
          >
            Force Complete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force check-out/check-in confirmation - records the step directly,
          independent of the Force Complete flow above. */}
      <Dialog open={forceStepAction !== null} onClose={handleCloseForceStepDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {forceStepAction === 'checkout' ? 'Force Check-out' : 'Force Check-in'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {forceStepAction === 'checkout'
              ? 'This records a check-out for this activity without an RFID exit scan. Please explain why.'
              : 'This records a check-in for this activity without an RFID entry scan. Please explain why.'}
          </DialogContentText>
          <TextField
            fullWidth
            required
            multiline
            rows={3}
            label="Comments"
            value={forceStepComments}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForceStepComments(e.target.value)}
            error={!forceStepComments.trim()}
            helperText={!forceStepComments.trim() ? 'Comments are required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForceStepDialog} disabled={forceStepSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleForceStepSubmit}
            disabled={forceStepSubmitting || !forceStepComments.trim()}
            variant="contained"
            color="warning"
          >
            {forceStepSubmitting ? 'Submitting...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ActivityExecutePage;